import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Spin, 
  Empty, 
  message,
  Badge,
  Space,
  Typography,
  Alert,
  Table
} from 'antd';
import {
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  WarningOutlined
} from '@ant-design/icons';
import attendanceManager from '../../firebase/attendanceService';
import staffAuthManager from '../../firebase/staffAuthService';
import dayjs from 'dayjs';
import './StaffAttendanceHistory.css';

const { Title, Text } = Typography;

const StaffAttendanceHistory = () => {
  // State management - simplified for month-based navigation
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Refs for component lifecycle management
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const timeoutRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  // Maximum retry attempts
  const MAX_RETRY_ATTEMPTS = 3;
  const LOADING_TIMEOUT = 30000; // 30 seconds timeout
  
  // Cleanup on unmount and handle window resize
  useEffect(() => {
    mountedRef.current = true; // Ensure it's set to true on mount
    
    // Handle window resize for responsive design
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      console.log('🧹 Component unmounting, cleaning up...');
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Authentication check
  const checkAuthentication = () => {
    console.log('🔐 Checking authentication...');
    
    // Check bypass mode first
    if (staffAuthManager.isBypassMode()) {
      console.log('✅ Bypass mode enabled');
      return { success: true, user: staffAuthManager.getMockEmployee() };
    }
    
    // Check regular authentication
    const currentEmployee = staffAuthManager.getCurrentEmployee();
    if (!currentEmployee) {
      console.log('❌ No authenticated employee found');
      return { 
        success: false, 
        error: 'Not authenticated. Please log in again.' 
      };
    }
    
    console.log('✅ Employee authenticated:', currentEmployee.email);
    return { success: true, user: currentEmployee };
  };

  // Enhanced data loading with timeout and retry logic
  const loadAttendanceData = async (startDate = null, endDate = null, showLoading = true) => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) {
      console.log('🚫 Load already in progress, skipping...');
      return;
    }
    
    loadingRef.current = true;
    
    if (showLoading) {
      setLoading(true);
    }
    
    setError(null);
    setAuthError(null);
    
    console.log('🔄 Loading attendance data...', { startDate, endDate, attempt: retryCount + 1 });
    
    // Set loading timeout
    timeoutRef.current = setTimeout(() => {
      if (loadingRef.current && mountedRef.current) {
        console.log('⏰ Loading timeout reached');
        loadingRef.current = false;
        setLoading(false);
        setError('Loading timeout. Please try again.');
      }
    }, LOADING_TIMEOUT);
    
    try {
      // Step 1: Check authentication
      const authCheck = checkAuthentication();
      if (!authCheck.success) {
        throw new Error(authCheck.error);
      }
      
      // Step 2: Load attendance history
      console.log('📋 Loading attendance history...');
      const historyResult = await attendanceManager.getAttendanceHistory(
        startDate,
        endDate,
        100 // Limit to 100 records
      );

      console.log('📊 History result:', historyResult);
      console.log('📊 Records data:', historyResult.records);
      console.log('📊 Records count:', historyResult.records?.length);
      console.log('🔍 Component mounted status:', mountedRef.current);

      // Check if component is still mounted before setting state
      if (!mountedRef.current) {
        console.log('🚫 Component unmounted during load, aborting...');
        loadingRef.current = false;
        return;
      }

      if (historyResult.success) {
        console.log('✅ Setting records to state:', historyResult.records?.length, 'records');
        setRecords(historyResult.records || []);
        setRetryCount(0); // Reset retry count on success
        
        console.log('✅ Data loading completed successfully');
      } else {
        console.error('❌ Failed to load attendance history:', historyResult.error);
        
        // Check if it's an authentication error
        if (historyResult.error.includes('not authenticated') || 
            historyResult.error.includes('Employee not authenticated')) {
          setAuthError(historyResult.error);
        } else {
          setError(historyResult.error);
        }
        
          // Retry logic for non-auth errors
          if (!historyResult.error.includes('authenticated') && retryCount < MAX_RETRY_ATTEMPTS && mountedRef.current) {
            console.log(`🔄 Retrying... (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
            setRetryCount(prev => prev + 1);
            setTimeout(() => {
              if (mountedRef.current) {
                loadAttendanceData(startDate, endDate, false);
              }
            }, 2000 * (retryCount + 1)); // Exponential backoff
            return;
          }
      }
    } catch (error) {
      console.error('❌ Error loading attendance data:', error);
      
      if (mountedRef.current) {
        if (error.message.includes('not authenticated') || 
            error.message.includes('Employee not authenticated')) {
          setAuthError(error.message);
        } else {
          setError(error.message);
          
          // Retry logic for network errors
          if (retryCount < MAX_RETRY_ATTEMPTS && 
              (error.message.includes('network') || error.message.includes('timeout')) &&
              mountedRef.current) {
            console.log(`🔄 Retrying due to network error... (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
            setRetryCount(prev => prev + 1);
            setTimeout(() => {
              if (mountedRef.current) {
                loadAttendanceData(startDate, endDate, false);
              }
            }, 2000 * (retryCount + 1));
            return;
          }
        }
      }
    } finally {
      loadingRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Always clear loading state if showLoading was true, regardless of mount status
      // This prevents the UI from getting stuck in loading state
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Month navigation handlers
  const handleMonthChange = useCallback((direction) => {
    const newMonth = direction === 'prev' 
      ? currentMonth.subtract(1, 'month')
      : currentMonth.add(1, 'month');
    
    console.log('📅 Month changed:', {
      direction,
      oldMonth: currentMonth.format('MMMM YYYY'),
      newMonth: newMonth.format('MMMM YYYY')
    });
    
    setCurrentMonth(newMonth);
    setRetryCount(0); // Reset retry count for new request
    
    // Load data for the new month
    const startDate = newMonth.startOf('month').toDate();
    const endDate = newMonth.endOf('month').toDate();
    loadAttendanceData(startDate, endDate);
  }, [currentMonth]);

  // Initialize with current month
  useEffect(() => {
    if (!initialLoadDone.current) {
      console.log('🚀 Initializing with current month...');
      
      // Mark initial load as started to prevent double execution
      initialLoadDone.current = true;
      
      // Load data for current month
      const startDate = currentMonth.startOf('month').toDate();
      const endDate = currentMonth.endOf('month').toDate();
      loadAttendanceData(startDate, endDate);
    }
  }, [currentMonth]);

  // Handle refresh
  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    setRetryCount(0); // Reset retry count
    const startDate = currentMonth.startOf('month').toDate();
    const endDate = currentMonth.endOf('month').toDate();
    loadAttendanceData(startDate, endDate);
  };

  // Handle authentication retry
  const handleAuthRetry = () => {
    console.log('🔄 Authentication retry triggered');
    setAuthError(null);
    setRetryCount(0);
    
    // Try to re-authenticate or reload
    if (staffAuthManager.isBypassMode()) {
      staffAuthManager.bypassLogin().then(() => {
        const startDate = currentMonth.startOf('month').toDate();
        const endDate = currentMonth.endOf('month').toDate();
        loadAttendanceData(startDate, endDate);
      });
    } else {
      // Redirect to login or show login prompt
      window.location.reload();
    }
  };


  // Helper functions
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'approved':
        return { 
          icon: <CheckCircleOutlined />, 
          text: 'Approved', 
          color: '#52c41a'
        };
      case 'pending':
        return { 
          icon: <ExclamationCircleOutlined />, 
          text: 'Pending', 
          color: '#faad14'
        };
      case 'rejected':
        return { 
          icon: <CloseCircleOutlined />, 
          text: 'Rejected', 
          color: '#ff4d4f'
        };
      default:
        return { 
          icon: <ExclamationCircleOutlined />, 
          text: 'Unknown', 
          color: '#d9d9d9'
        };
    }
  };

  const getTypeDisplay = (type) => {
    return type === 'clock-in' 
      ? { icon: '🟢', text: 'Clock In', color: '#52c41a' }
      : { icon: '🔴', text: 'Clock Out', color: '#ff4d4f' };
  };

  const getLocationDisplay = (isWarehouse) => {
    return isWarehouse 
      ? { icon: '🏢', text: 'Warehouse', color: '#a20000' }
      : { icon: '🌍', text: 'Job-site', color: '#722ed1' };
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-SG', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-SG', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-SG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Process records to group clock-in and clock-out pairs
  const processAttendanceRecords = (records) => {
    const processedRecords = [];
    const recordsByDate = {};

    // Group records by date
    records.forEach(record => {
      const dateKey = new Date(record.timestamp).toDateString();
      if (!recordsByDate[dateKey]) {
        recordsByDate[dateKey] = [];
      }
      recordsByDate[dateKey].push(record);
    });

    // Process each date group
    Object.keys(recordsByDate).forEach(dateKey => {
      const dayRecords = recordsByDate[dateKey];
      const clockInRecords = dayRecords.filter(r => r.type === 'clock-in');
      const clockOutRecords = dayRecords.filter(r => r.type === 'clock-out');

      // If there are clock-in records
      clockInRecords.forEach(clockIn => {
        // Find matching clock-out (closest time after clock-in)
        const matchingClockOut = clockOutRecords
          .filter(clockOut => new Date(clockOut.timestamp) > new Date(clockIn.timestamp))
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))[0];

        processedRecords.push({
          key: clockIn.id,
          date: clockIn.timestamp,
          clockInTime: clockIn.timestamp,
          clockOutTime: matchingClockOut ? matchingClockOut.timestamp : null,
          clockInLocation: getLocationDisplay(clockIn.isWarehouse).text,
          clockOutLocation: matchingClockOut ? getLocationDisplay(matchingClockOut.isWarehouse).text : 'Pending',
          clockInRecord: clockIn,
          clockOutRecord: matchingClockOut
        });
      });

      // Handle orphaned clock-out records (clock-out without clock-in)
      clockOutRecords.forEach(clockOut => {
        const hasMatchingClockIn = clockInRecords.some(clockIn => 
          new Date(clockIn.timestamp) < new Date(clockOut.timestamp)
        );
        
        if (!hasMatchingClockIn) {
          processedRecords.push({
            key: clockOut.id,
            date: clockOut.timestamp,
            clockInTime: null,
            clockOutTime: clockOut.timestamp,
            clockInLocation: 'N/A',
            clockOutLocation: getLocationDisplay(clockOut.isWarehouse).text,
            clockInRecord: null,
            clockOutRecord: clockOut
          });
        }
      });
    });

    // Sort by date (newest first)
    return processedRecords.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Authentication error state
  if (authError && !loading) {
    return (
      <div className="staff-attendance-history">
        <Alert
          message="Authentication Required"
          description={authError}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          action={
            <Button type="primary" onClick={handleAuthRetry}>
              Login Again
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      </div>
    );
  }

  // Error state
  if (error && !loading) {
    return (
      <div className="staff-attendance-history">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: 16 }} />
            <Title level={4} type="danger">Failed to Load Data</Title>
            <Text type="secondary">{error}</Text>
            {retryCount > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Attempted {retryCount} time{retryCount > 1 ? 's' : ''}
                </Text>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <Button 
                type="primary" 
                onClick={handleRefresh} 
                icon={<ReloadOutlined />}
                loading={loadingRef.current}
              >
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="staff-attendance-history">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading data...</Text>
            {retryCount > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Retry attempt {retryCount}/{MAX_RETRY_ATTEMPTS}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-attendance-history">
      {/* Month Navigation */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" justify="center" style={{ position: 'relative' }}>
          <Col>
            <Space size="middle" align="center">
              <Button 
                icon={<LeftOutlined />} 
                onClick={() => handleMonthChange('prev')} 
                size="small"
                type="text"
              />
              <Text strong style={{ minWidth: 150, textAlign: 'center', display: 'inline-block', fontSize: '16px' }}>
                {currentMonth.format('MMMM YYYY')}
              </Text>
              <Button 
                icon={<RightOutlined />} 
                onClick={() => handleMonthChange('next')} 
                size="small"
                type="text"
              />
            </Space>
          </Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            size="small"
            title="Refresh"
            loading={loadingRef.current}
            style={{ position: 'absolute', right: 0 }}
          />
        </Row>
      </Card>

      {/* Records List */}
      <Card 
        title={
          <Space>
            <CalendarOutlined />
            <span>Attendance Records</span>
            <Badge count={processAttendanceRecords(records).length} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentMonth.format('MMMM YYYY')}
          </Text>
        }
      >
        {/* Mobile-friendly view for small screens */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {(() => {
              const sortedRecords = processAttendanceRecords(records).sort((a, b) => new Date(a.date) - new Date(b.date));
              const recordsWithDayIndex = sortedRecords.map((record, index) => ({
                ...record,
                dayIndex: index + 1
              }));
              return recordsWithDayIndex
                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort newest first for display
                .map((record) => (
              <Card 
                key={record.key} 
                size="small" 
                style={{ 
                  borderRadius: '8px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Date Header */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '8px'
                  }}>
                    <Text strong style={{ fontSize: '14px', color: '#a20000' }}>
                      {formatDate(record.date)}
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                      Day {record.dayIndex}
                    </Text>
                  </div>
                  
                  {/* Clock In/Out Times */}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                        Clock In
                      </Text>
                      <Text style={{ 
                        fontSize: '14px', 
                        fontWeight: '600',
                        color: record.clockInTime ? '#52c41a' : '#d9d9d9' 
                      }}>
                        {record.clockInTime ? formatTime(record.clockInTime) : 'N/A'}
                      </Text>
                      <Text style={{ fontSize: '10px', color: '#999' }}>
                        {record.clockInLocation === 'Warehouse' && '🏢 '}
                        {record.clockInLocation === 'Job-site' && '🌍 '}
                        {record.clockInLocation}
                      </Text>
                    </div>
                    
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <Text style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                        Clock Out
                      </Text>
                      {record.clockOutTime ? (
                        <>
                          <Text style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#ff4d4f'
                          }}>
                            {formatTime(record.clockOutTime)}
                          </Text>
                          <Text style={{ fontSize: '10px', color: '#999' }}>
                            {record.clockOutLocation === 'Warehouse' && '🏢 '}
                            {record.clockOutLocation === 'Job-site' && '🌍 '}
                            {record.clockOutLocation}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#faad14'
                          }}>
                            Pending
                          </Text>
                          <Text style={{ fontSize: '10px', color: '#999' }}>
                            {/* Single pending message */}
                          </Text>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Working Hours (if both times available) */}
                  {record.clockInTime && record.clockOutTime && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '4px 8px', 
                      background: '#f6ffed', 
                      borderRadius: '4px',
                      border: '1px solid #b7eb8f'
                    }}>
                      <Text style={{ fontSize: '11px', color: '#52c41a', fontWeight: '600' }}>
                        Total: {((new Date(record.clockOutTime) - new Date(record.clockInTime)) / (1000 * 60 * 60)).toFixed(1)}h
                      </Text>
                    </div>
                  )}
                </div>
              </Card>
                ));
            })()}
            
            {processAttendanceRecords(records).length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    No attendance records found
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Try adjusting your date range
                    </Text>
                  </span>
                }
              />
            )}
          </div>
        ) : (
          /* Desktop Table View */
          <Table
            dataSource={processAttendanceRecords(records)}
            pagination={false} // Remove pagination to show all records
            scroll={{ x: 800 }}
            size="small"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      No attendance records found
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Try adjusting your date range
                      </Text>
                    </span>
                  }
                />
              )
            }}
            columns={[
              {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                width: 120,
                render: (date) => (
                  <Text strong style={{ fontSize: '13px' }}>
                    {formatDate(date)}
                  </Text>
                ),
                sorter: (a, b) => new Date(a.date) - new Date(b.date), // Changed to ascending (oldest first)
                defaultSortOrder: 'ascend'
              },
              {
                title: 'Clock In Time',
                dataIndex: 'clockInTime',
                key: 'clockInTime',
                width: 100,
                render: (time) => (
                  <Text style={{ fontSize: '13px', color: time ? '#52c41a' : '#d9d9d9' }}>
                    {time ? formatTime(time) : 'N/A'}
                  </Text>
                )
              },
              {
                title: 'Clock Out Time',
                dataIndex: 'clockOutTime',
                key: 'clockOutTime',
                width: 100,
                render: (time) => (
                  <Text style={{ 
                    fontSize: '13px', 
                    color: time ? '#ff4d4f' : '#faad14',
                    fontWeight: time ? 'normal' : 'bold'
                  }}>
                    {time ? formatTime(time) : 'Pending'}
                  </Text>
                )
              },
              {
                title: 'Clock In Location',
                dataIndex: 'clockInLocation',
                key: 'clockInLocation',
                width: 120,
                render: (location) => (
                  <Text style={{ fontSize: '12px' }}>
                    {location === 'Warehouse' && '🏢 '}
                    {location === 'Job-site' && '🌍 '}
                    {location}
                  </Text>
                )
              },
              {
                title: 'Clock Out Location',
                dataIndex: 'clockOutLocation',
                key: 'clockOutLocation',
                width: 120,
                render: (location) => (
                  <Text style={{ 
                    fontSize: '12px',
                    color: location === 'Pending' ? '#faad14' : 'inherit',
                    fontWeight: location === 'Pending' ? 'bold' : 'normal'
                  }}>
                    {location === 'Warehouse' && '🏢 '}
                    {location === 'Job-site' && '🌍 '}
                    {location}
                  </Text>
                )
              },
              {
                title: 'Hours',
                key: 'hours',
                width: 80,
                render: (_, record) => {
                  if (record.clockInTime && record.clockOutTime) {
                    const hours = ((new Date(record.clockOutTime) - new Date(record.clockInTime)) / (1000 * 60 * 60)).toFixed(1);
                    return (
                      <Text style={{ fontSize: '12px', color: '#52c41a', fontWeight: '600' }}>
                        {hours}h
                      </Text>
                    );
                  }
                  return <Text style={{ fontSize: '12px', color: '#d9d9d9' }}>-</Text>;
                }
              }
            ]}
          />
        )}
      </Card>

    </div>
  );
};

export default StaffAttendanceHistory;
